import { Box, Button, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { FC, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  useGetPartnerContactQuery,
  usePutPartnerContactMutation,
} from "src/entities/partners/model/api/partners";
import {
  getPartnerContactFullFormValues,
  getPostPartnerContactDTO,
} from "src/entities/partners/model/helpers/helpers";
import {
  LoadingButton,
  ModalComponent,
  EConfirmModalType,
} from "src/shared/components";
import { modalText } from "src/shared/constants/form.constants";
import { useConfirmModal } from "src/shared/hooks";
import { IPartnerContactFormValues } from "../../model/types";
import { PartnerContactForm } from "./PartnerContactForm";

interface IEditContactModalProps {
  open: boolean;
  onClose: () => void;
  contactId: number;
}

export const EditContactModal: FC<IEditContactModalProps> = ({
  contactId,
  onClose,
  open,
}) => {
  const { partner_id } = useParams<{ partner_id: string }>();
  const formRef = useRef<HTMLFormElement>(null);

  const { data: contact, isFetching } = useGetPartnerContactQuery(
    partner_id && contactId
      ? {
          partner_id,
          id: String(contactId),
        }
      : skipToken,
  );
  const [putPartner, { isLoading }] = usePutPartnerContactMutation();

  const handleSave = async (values: IPartnerContactFormValues) => {
    try {
      const { disable_pcp_access_reason, ...rest } = values;
      const preparedData: IPartnerContactFormValues = {
        ...rest,
        ...(disable_pcp_access_reason && { disable_pcp_access_reason }),
      };

      await putPartner(getPostPartnerContactDTO(preparedData)).unwrap();
      onClose();
    } catch (e) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
      console.log(e);
    }
  };

  const { modal, showModal } = useConfirmModal({
    onResolve: onClose,
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: { isLoading: false, title: modalText.continue },
    children: <Typography>{modalText.unsavedChanges}</Typography>,
    title: modalText.confirmAction,
  });

  const hasPartnerProgramConsent =
    contact?.consents?.some(
      (consent) => consent.consent_type === "partner_program",
    ) ?? false;

  return (
    <>
      {modal}
      <ModalComponent
        width={450}
        disableBodyPadding
        onClose={showModal}
        open={open}
        title={`Контакт ${contact?.lastname} ${contact?.firstname}`}
        isLoading={isFetching}
        modalFooter={
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button variant="outlined" onClick={showModal}>
              Отмена
            </Button>
            <LoadingButton
              isLoading={isLoading}
              variant="contained"
              onClick={() => {
                formRef.current?.submit();
              }}
            >
              Сохранить
            </LoadingButton>
          </Box>
        }
      >
        <PartnerContactForm
          ref={formRef}
          contactPerson={
            contact ? getPartnerContactFullFormValues(contact) : undefined
          }
          handleSave={handleSave}
          hasPartnerProgramConsent={hasPartnerProgramConsent}
        />
      </ModalComponent>
    </>
  );
};
